import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Ticket, Copy, Trash, Plus } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalCard, BrutalInput } from "./Brutal";
import { copyToClipboard } from "../lib/clipboard";

const money = (v) => `$${Number(v) % 1 === 0 ? Number(v) : Number(v).toFixed(2)}`;

/** v413.0 — one-time discount codes a distributor can mint and share.
 *  Single-use, no expiry; the distributor absorbs the discount. */
export const DiscountCodesCard = ({ asParams = {} }) => {
  const { t } = useTranslation();
  const [codes, setCodes] = useState(null);
  const [creating, setCreating] = useState(false);
  const [kind, setKind] = useState("percent");
  const [value, setValue] = useState("");
  const [multiUse, setMultiUse] = useState(false);
  const [expDays, setExpDays] = useState("");
  const [customCode, setCustomCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/discount-codes", { params: asParams })
      .then(({ data }) => setCodes(data || []))
      .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const create = async (e) => {
    e.preventDefault();
    const v = Number(value);
    if (!Number.isFinite(v) || v <= 0) { toast.error(t("discounts.invalid_value")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/discount-codes", {
        kind, value: v,
        multi_use: multiUse,
        expires_days: expDays ? Number(expDays) : null,
        custom_code: customCode.trim() || null,
      }, { params: asParams });
      toast.success(t("discounts.created", { code: data.code }));
      copyToClipboard(data.code, t("discounts.copied"));
      setValue(""); setCustomCode(""); setExpDays(""); setMultiUse(false);
      setCreating(false);
      load();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const voidCode = async (c) => {
    if (!window.confirm(t("discounts.void_confirm", { code: c.code }))) return;
    try {
      await api.delete(`/discount-codes/${c.id}`, { params: asParams });
      toast.success(t("discounts.voided"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <BrutalCard data-testid="discount-codes-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base md:text-lg font-semibold flex items-center gap-2">
            <Ticket size={18} weight="bold" className="text-[#8a6a1c]" />
            {t("discounts.title")}
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">{t("discounts.subtitle")}</p>
        </div>
        <BrutalButton size="sm" onClick={() => setCreating((v) => !v)} data-testid="discount-create-toggle">
          <Plus size={14} weight="bold" /> {t("discounts.new_btn")}
        </BrutalButton>
      </div>

      {creating && (
        <form onSubmit={create} className="mt-3 p-3 rounded-lg border border-[#c9a24a]/50 bg-[#fbf6e7]/50 space-y-2.5">
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setKind("percent")}
              className={`rounded-lg px-3 py-2 text-sm font-bold border-2 ${kind === "percent" ? "border-[#c9a24a] bg-white" : "border-zinc-200 bg-white/60 text-zinc-500"}`}
              data-testid="discount-kind-percent">
              % {t("discounts.kind_percent")}
            </button>
            <button type="button" onClick={() => setKind("amount")}
              className={`rounded-lg px-3 py-2 text-sm font-bold border-2 ${kind === "amount" ? "border-[#c9a24a] bg-white" : "border-zinc-200 bg-white/60 text-zinc-500"}`}
              data-testid="discount-kind-amount">
              $ {t("discounts.kind_amount")}
            </button>
          </div>
          <BrutalInput
            type="number" min="0.01" step="0.01" max={kind === "percent" ? 100 : undefined}
            value={value} onChange={(e) => setValue(e.target.value)}
            placeholder={kind === "percent" ? t("discounts.value_ph_percent") : t("discounts.value_ph_amount")}
            data-testid="discount-value-input"
          />
          <div className="grid grid-cols-2 gap-2">
            <BrutalInput
              value={customCode} onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
              placeholder={t("discounts.custom_code_ph")} maxLength={24}
              data-testid="discount-custom-code-input"
            />
            <BrutalInput
              type="number" min="1" max="365" step="1"
              value={expDays} onChange={(e) => setExpDays(e.target.value)}
              placeholder={t("discounts.expires_days_ph")}
              data-testid="discount-expires-input"
            />
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer select-none" data-testid="discount-multi-use-label">
            <input type="checkbox" checked={multiUse} onChange={(e) => setMultiUse(e.target.checked)}
              className="accent-[#c9a24a] w-4 h-4" data-testid="discount-multi-use-checkbox" />
            {t("discounts.multi_use_label")}
          </label>
          <BrutalButton type="submit" size="sm" disabled={busy} className="w-full" data-testid="discount-create-submit">
            {busy ? t("common.saving") : t("discounts.create_submit")}
          </BrutalButton>
        </form>
      )}

      <div className="mt-3 space-y-1.5">
        {codes === null ? (
          <p className="text-sm text-zinc-500">{t("common.loading")}</p>
        ) : codes.length === 0 ? (
          <p className="text-sm text-zinc-500" data-testid="discount-empty">{t("discounts.empty")}</p>
        ) : codes.map((c) => {
          const expired = c.expires_at && c.expires_at < new Date().toISOString();
          return (
          <div key={c.id}
            className={`flex items-center gap-2 flex-wrap rounded-lg border px-3 py-2 ${c.used_at || expired ? "border-zinc-200 bg-zinc-50 opacity-70" : "border-[#c9a24a]/50 bg-white"}`}
            data-testid={`discount-row-${c.code}`}>
            <span className="font-mono text-sm font-bold tracking-wide">{c.code}</span>
            <span className="text-xs font-semibold text-[#8a6a1c]">
              {c.kind === "percent" ? `${c.value}%` : money(c.value)} {t("discounts.off")}
            </span>
            {c.multi_use && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#fbf6e7] text-[#8a6a1c] border border-[#c9a24a]/40" data-testid={`discount-multi-${c.code}`}>
                {t("discounts.badge_multi", { n: c.use_count || 0 })}
              </span>
            )}
            {c.expires_at && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${expired ? "bg-rose-100 text-rose-700" : "bg-zinc-100 text-zinc-600"}`} data-testid={`discount-expiry-${c.code}`}>
                {expired ? t("discounts.expired") : t("discounts.expires", { date: c.expires_at.slice(0, 10) })}
              </span>
            )}
            <span className="flex-1" />
            {c.used_at ? (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-zinc-200 text-zinc-600" title={c.used_reference || ""}>
                {t("discounts.used")}{c.used_reference ? ` · ${c.used_reference}` : ""}
              </span>
            ) : (
              <>
                <button type="button" onClick={() => copyToClipboard(c.code, t("discounts.copied"))}
                  className="p-1.5 rounded text-[#8a6a1c] hover:bg-[#fbf6e7]" title={t("common.copy")}
                  data-testid={`discount-copy-${c.code}`}>
                  <Copy size={14} weight="bold" />
                </button>
                <button type="button" onClick={() => voidCode(c)}
                  className="p-1.5 rounded text-rose-600 hover:bg-rose-50" title={t("discounts.void")}
                  data-testid={`discount-void-${c.code}`}>
                  <Trash size={14} weight="bold" />
                </button>
              </>
            )}
          </div>
        );})}
      </div>
    </BrutalCard>
  );
};
